import classnames from 'classnames'
import { useCallback, useEffect, useMemo } from 'react'

import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import socialSectionStateManager from '@src/quince-site-common/src/redux/globalStateManager/SocialSectionStateManager'
import getContentContainerDetails from '@src/quince-site-common/src/utils/getContentContainerDetails'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { useInfluencerCardProducts } from '../../hooks/useInfluencerCardProducts'
import { RichText, RichTextProps } from '../../molecules'
import {
  getIndividualTagStatus,
  getInitialSelectedOptions,
  getProductTagsList,
  isNestedProduct,
} from '../../molecules/productCard/productCard.helpers'
import InfluencerCard from '../influencerCard'
import Swiper from '../swiper'
import { ARROW_COLOR } from '../swiper/Swiper'

import styles from './influencerSection.module.scss'

export interface InfluencerSectionProps {
  influencerDetailList: InfluencerDetails[]
  headingRichText?: RichTextProps
  descriptionRichText?: RichTextProps
  backgroundColor?: string
  className?: string
  title?: string
  trackingMetaData?: TrackingMetaData
  productViewTracker?: string
}

const InfluencerSection = ({
  influencerDetailList,
  headingRichText,
  descriptionRichText,
  className = '',
  title,
  trackingMetaData,
  productViewTracker,
}: InfluencerSectionProps) => {
  const { isB2BSelfService } = useStorefrontModeContext()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const filteredInfluencerDetailList = useMemo(
    () =>
      influencerDetailList.filter(influencerDetail =>
        isB2BSelfService ? influencerDetail.contentfulProductId : influencerDetail.spid,
      ),
    [influencerDetailList],
  )

  const { isLoadingProducts, products, spidToProductMap, contentfulProductIdToProductMap } = useInfluencerCardProducts(
    filteredInfluencerDetailList,
    isB2BSelfService,
  )

  useEffect(() => {
    socialSectionStateManager.updateProductsAndLoadingState(isLoadingProducts, products)
  }, [isLoadingProducts, products])

  const classes = classnames(styles.productCarouselWrap, className)

  const handleItemClick = useCallback(
    (influencerIndex: number) => {
      const product = products?.[0]
      const contentContainerDetails = getContentContainerDetails(trackingMetaData)
      const influencerDetail = filteredInfluencerDetailList[influencerIndex]

      if (product && influencerDetail) {
        const selectedOptions = getInitialSelectedOptions(
          product,
          [...influencerDetail.selectedProductOptions],
          true,
          false,
          isNestedProduct(product) ? 2 : 1,
        )
        const productTags = getProductTagsList(product.tags, selectedOptions)

        trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
          context: {
            ...trackingMetaData?.context,
            contentContainerDisplayName: contentContainerDetails.contentContainerDisplayName,
            contentContainerType: contentContainerDetails.contentContainerType,
            contentContainerName: contentContainerDetails.contentContainerName,
            productPosition: influencerIndex,
            tags: productTags,
            tag_ids: transformTagsIntoAnalyticsTags(productTags),
            ...getIndividualTagStatus(productTags),
          },
        })
      }

      socialSectionStateManager.showSocialSectionPopUp({
        influencerDetails: filteredInfluencerDetailList,
        influencerIndex: influencerIndex,
        isLoadingProducts,
        products,
      })
    },
    [products, filteredInfluencerDetailList, isLoadingProducts, trackingMetaData],
  )

  if (!filteredInfluencerDetailList.length) return <></>
  return (
    <div id={title?.toLowerCase()} className={classes}>
      <div
        className={classnames(
          'd-flex flex-d--column align-items--center padding-h--30',
          styles.influencerSection_heading_newDesignV2,
        )}
      >
        {headingRichText && <RichText {...headingRichText} />}
        {descriptionRichText && <RichText {...descriptionRichText} />}
      </div>
      <div className={classnames(styles.influencerSliderWrapper, styles.influencerSliderWrapper_newDesignV2)}>
        {filteredInfluencerDetailList && (
          <Swiper
            showDots={false}
            variableWidth
            showNavigator={isDesktop}
            numberOfVisibleSlides={isDesktop ? 4 : 1}
            className={styles.influencerSlider}
            arrowColor={ARROW_COLOR.WHITE}
            id={'influencerSection'}
            virtualOptions={{ enabled: true }}
            // @i18n-ignore
            tracker="influencer-section"
            trackingMetaData={trackingMetaData}
          >
            {filteredInfluencerDetailList.map((influencerDetails, index) => {
              const productDetails = isB2BSelfService
                ? contentfulProductIdToProductMap[influencerDetails.contentfulProductId ?? '']
                : influencerDetails.spid
                  ? spidToProductMap[influencerDetails.spid]
                  : undefined

              return (
                <InfluencerCard
                  onClick={() => {
                    handleItemClick(index)
                  }}
                  key={influencerDetails.id}
                  influencerDetails={{ ...influencerDetails }}
                  productViewTracker={productViewTracker}
                  productDetails={productDetails}
                />
              )
            })}
          </Swiper>
        )}
      </div>
    </div>
  )
}

export default InfluencerSection
